import { Route, Router, Routes, hashIntegration, useParams } from "@solidjs/router";
import { Component, JSX, createSignal, onMount } from "solid-js";
import { EditArtworkData, ArtworkImageData, Ad, LivingArtistArtwork, CompressedSelectList, UserSession, AtelierImage, ImageType } from "./types";
import { Details } from "./pages/details";
import { Faculty } from "./pages/faculty";
import { EditAtelierImage } from "./pages/edit-atelier-image";
import { Introduction } from "./pages/introduction";
import { fetchGet } from "./library/common";
import { EditImage } from "./pages/edit-image";
import { JetMenu } from "./components/jetmenu/jetmenu";
import { LogoSlider } from "./sections/logo-slider";
import { Footer } from "./sections/footer";
import { createStore } from "solid-js/store";
import toast, { Toaster } from "solid-toast";
import { CMSBlock, CMSProvider } from "./components/cms/provider";
import { plainText } from "./components/cms/cms";
import _ from "underscore";
import { getOrSetAsync } from "./API";
import { CreditCardPage } from "./pages/credit-card";
import { PaymentProcessedPage } from "./pages/payment-processed";
import { PaypalConfirmedPage } from "./pages/paypal-confirmed";
import { GoogleMapsProvider } from "./components/GoogleMapsProvider";
import { Payment } from "./pages/payment";
import { InstructorWorks } from "./pages/instructor-works";
import { StudentWorks } from "./pages/student-works";
import { OtherImages } from "./pages/other-images";
import { EditFacultyMember } from "./pages/edit-faculty-member";
import { Complete } from "./pages/complete";
import { GoogleMapsGeocoderProvider } from "./components/google-maps-geocoder-provider";

export const limits = {
    faculty: {
        min: 1, max: 15
    },
    instructorWorks: {
        min: 4, max: 30
    },
    studentWorks: {
        min: 10, max: 30
    },
    otherImages: {
        min: 2, max: 30
    }
}
export const [headerContent, setHeaderContent] = createSignal<JSX.Element>(<></>);
export const [ads, setAds] = createSignal<Ad[]>([]);
export const [cms, setCms] = createStore<Record<string,CMSBlock>>({});
export const [cache, setCache] = createStore<Record<string,any>>({});
export const getOrSetCache = async (key: string, getter: () => Promise<any>) => {
    const item = cache[key];
    if (item != undefined) return item;
    const value = await getter();
    setCache(key, value);
    return value;
};

export const loadCms = async (ids: string[]) => {
    const cachedIds = Object.keys(cms);
    const uncachedIds = ids.filter(id => !cachedIds.some(key => key == id));
    if (!uncachedIds.length) return;
    let json: any;
    try {
        const res = await fetchGet("/Home/GetContentBlocksJson", { ids: uncachedIds.join(",") });
        json = await res.json();
        if (!json.Success) return;
    } catch (ex) {
        console.log("loadCms", { ex });
        return;
    }
    let data: Record<string, CMSBlock> = {};
    uncachedIds.forEach(id => {
        let value = json.Data[id];
        if (!value || value.length === 0) {
            data[id] = { found: false, item: null };
            return;
        }
        data[id] = { found: true, item: value };
    });
    setCms(data);
};
export const formatText = (text: string, data: Record<string,any>) => {
    console.log("formatText", { text, data });
    for (let key in data) text = text?.replace(new RegExp('{' + key + '}', 'gi'), data[key]);
    return text;
}
export const getText = (id: string, backup?: string, data?: Record<string, any>) => {
    const html = cms[id]?.item;
    if (!html) {
        return data && backup ? formatText(backup, data) : backup || "";
    }
    let text = plainText(html);
    if (data) {
        text = formatText(text, data);
    }
    return text;
}
export const loadCreditCardCms = async () => {
    const ids = [
        "label::postal-code-zip",
        "label::credit-card-number",
        "label::expiry-mm-yy",
        "label::ccv",
        "label::street",
        "label::pay-with-credit-card",
        "button::cancel",
        "button::pay-now",
        "salon-application/credit-card::total-owing"
    ];
    await loadCms(ids);
}
export const loadIntroductionCms = async () => {
    await loadCms([
        
    ]);
}
export const loadDetailsCms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadPaymentCms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadFacultyCms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadInstructorWorksCms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadStudentWorksCms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const getPlainText = (id: string, backup?: string) => {
    const html = cms[id]?.item;
    if (!html) return backup || "";
    return plainText(html);
}
enum Claim {
    IsLoggedIn,
    Paid,
    Unpaid,
    CanApply,
    IsRegistered,
    HasMinimumAll,
    HasMinimumFaculty,
    HasMinimumInstructorWorks,
    HasMinimumStudentWorks,
    HasMinimumOtherImages
}
export const getApplicationStatusJson = async (options?: { forceRefresh: boolean }) => getOrSetAsync("application-status", async () => {
    const res = await fetch("/AtelierApplication/GetApplicationStatusJson");
    const json = await res.json();
    return json;
}, options);
export const getArtworksCountJson = async (options?: { forceRefresh: boolean }) => getOrSetAsync("artworks-count", async () => {
    const res = await fetch("/AtelierApplication/GetArtworkCountJson");
    const json = await res.json();
    return json;
}, options);
export const App : Component = () => {
    const authorised = async (claims: Claim[]) => {
        const json = await getApplicationStatusJson();
        if (!json.Success) {
            toast.error("Unable to contact the server just now. Please try reloading the page.");
            return false;
        }
        const data = json.Data;
        if (claims.some(c => c == Claim.IsLoggedIn)) {
            if (!data.isLoggedIn) {
                toast.error("You must be logged in to access this page.");
                return false;
            }
        }
        if (claims.some(c => c == Claim.CanApply)) {
            if (!data.canApply) {
                if (data.isAdmin) {
                    toast.error("The ARC Atelier Application has not yet opened: but Admin is granted access.");
                    return true;
                }
                toast.error("The ARC Atelier Application has not yet opened.");
                return false;
            }
        }
        if (claims.some(c => c == Claim.IsRegistered)) {
            if (!data.isRegistered) {
                toast.error("You must complete the profile form on Step 1 to continue.");
                return false;
            }
        }
        const atelierId = json.Data.atelierId;
        if (claims.some(c => c == Claim.HasMinimumAll || c == Claim.HasMinimumFaculty)) {
            const count = (await getFacultyMembers(atelierId)).length;
            if (count < limits.faculty.min) {
                toast.error(`You must submit at least ${limits.faculty.min} faculty members.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.HasMinimumAll || c == Claim.HasMinimumInstructorWorks)) {
            const count = (await getInstructorWorks(atelierId)).length;
            if (count < limits.instructorWorks.min) {
                toast.error(`You must submit at least ${limits.instructorWorks.min} instructor works.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.HasMinimumAll || c == Claim.HasMinimumStudentWorks)) {
            const studentWorksTotal = (await getStudentWorks(atelierId)).length;
            if (studentWorksTotal > limits.studentWorks.max) {
                toast.error(`You may submit no more than ${limits.studentWorks.max} student works.`);
                return false;
            }
            if (studentWorksTotal == 0) {
                const instructorWorksTotal = (await getInstructorWorks(atelierId)).length;
                const facultyMembersTotal = (await getFacultyMembers(atelierId)).length;
                const minimumInstructorWorks = facultyMembersTotal * 5;
                if (instructorWorksTotal >= minimumInstructorWorks) {
                    return true;
                }
                toast.error(`If you have no student works to add at this stage you must submit at least 5 instructor works per faculty member (i.e., a minimum of ${minimumInstructorWorks} instructor works for your application).`);
                return false;
            }
            if (studentWorksTotal < limits.studentWorks.min) {
                toast.error(`You must submit at least ${limits.studentWorks.min} student works.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.HasMinimumAll || c == Claim.HasMinimumOtherImages)) {
            const count = (await getOtherImages(atelierId)).length;
            if (count < limits.otherImages.min) {
                toast.error(`You must submit at least ${limits.otherImages.min} images of the atelier space.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.Unpaid)) {
            const completed = ["Paid","Judging","Accepted", "Reviewing"];
            console.log({ claims, completed, status: data.applicationStatus });
            if (!completed.some(x => x == data.applicationStatus)) {
                toast.error(`You must complete payment for the application.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.Paid)) {
            const completed = ["Judging","Accepted", "Reviewing"];
            console.log({ claims, completed, status: data.applicationStatus });
            if (!completed.some(x => x == data.applicationStatus)) {
                toast.error(`The application is already complete.`);
                return false;
            }
        }
        return true;
    }
    const getProfile = async () => {
        const res = await fetch("/AtelierApplication/GetProfileJson");
        const json = await res.json();
        return json.Data;
    }
    const getProfileResources = async () => getOrSetCache("/AtelierApplication/GetProfileResourcesJson", async () => {
        const res = await fetch("/AtelierApplication/GetProfileResourcesJson");
        const json = await res.json();
        return json.Data;
    });
    const uncompress = (list: CompressedSelectList) => list.map(item => ({ Value: item[0], Text: item[1] }));
    const loadDetails = async ({ navigate }: any) => {
        if (!await authorised([Claim.IsLoggedIn])) {
            return navigate("/not-logged-in");
        }
        await loadDetailsCms();
        const step1 = await getProfile();
        const resources = await getProfileResources();
        return {
            step1,            
            resources
        } as any;
    };
    const loadInstructorWorks = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/details");
            }
            if (!await authorised([Claim.HasMinimumFaculty])) {
                return navigate("/faculty");
            }
            await loadInstructorWorksCms();
            const session = await getApplicationStatusJson();
            const atelierId = session.Data.atelierId;
            const atelierImages = await getInstructorWorks(atelierId);
            return {
                atelierImages
            }
        } catch (ex) {
            log("loadInstructorWorks", { ex });
            navigate("/error", { state: { ex } });
        }
    };
    const loadStudentWorks = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/details");
            }
            if (!await authorised([Claim.HasMinimumFaculty])) {
                return navigate("/faculty");
            }
            if (!await authorised([Claim.HasMinimumInstructorWorks])) {
                return navigate("/instructor-works");
            }
            await loadStudentWorksCms();
            const session = await getApplicationStatusJson();
            const atelierId = session.Data.atelierId;
            const atelierImages = await getStudentWorks(atelierId);
            return {
                atelier: {
                    Id: atelierId
                },
                atelierImages
            }
        } catch (ex) {
            log("loadStudentWorks", { ex });
            navigate("/error", { state: { ex } });
        }
    };
    const loadOtherImages = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/details");
            }
            if (!await authorised([Claim.HasMinimumFaculty])) {
                return navigate("/faculty");
            }
            if (!await authorised([Claim.HasMinimumInstructorWorks])) {
                return navigate("/instructor-works");
            }
            if (!await authorised([Claim.HasMinimumStudentWorks])) {
                return navigate("/student-works");
            }
            const session = await getApplicationStatusJson();
            const atelierId = session.Data.atelierId;
            const atelierImages = await getOtherImages(atelierId);
            return {
                atelierImages
            }
        } catch (ex) {
            log("loadStudentWorks", { ex });
            navigate("/error", { state: { ex } });
        }
    };
    type ClaimMatrix = [ claim: Claim, failRoute: string, successRoute?: string ];
    const authenticate = async (navigate: any, matrix: ClaimMatrix[]) => {
        const len = matrix.length;
        for (let i = 0; i < len; i++) {
            if (!await authorised([matrix[i][0]])) {
                return navigate(matrix[i][1]);
            } else {
                if (matrix[i].length > 2) {
                    return navigate(matrix[i][2]);
                }
            }
        }
    }
    const loadPayment = async ({ navigate }: any) => {
        await authenticate(navigate, [
            [Claim.IsLoggedIn, "/not-logged-in"],
            [Claim.IsRegistered, "/details"],
            [Claim.HasMinimumFaculty, "/faculty"],
            [Claim.HasMinimumInstructorWorks, "/instructor-works"],
            [Claim.HasMinimumStudentWorks, "/student-works"],
            [Claim.HasMinimumOtherImages, "/other-images"],
            [Claim.Paid, "", "/complete"]
        ]);
    };
    const loadComplete = async ({ navigate }: any) => {
        await authenticate(navigate, [
            [Claim.IsLoggedIn, "/not-logged-in"],
            [Claim.IsRegistered, "/details"],
            [Claim.HasMinimumFaculty, "/faculty"],
            [Claim.HasMinimumInstructorWorks, "/instructor-works"],
            [Claim.HasMinimumStudentWorks, "/student-works"],
            [Claim.HasMinimumOtherImages, "/other-images"],
            [Claim.Unpaid, "/payment"]
        ]);
        const session = (await getApplicationStatusJson()).Data as UserSession;
        return {
            session
        };
    };
    const getFacultyMembers = async (id: number) => {
        const res = await fetchGet("/AtelierApplication/GetFacultyMembersJson", { id });
        const json = await res.json();
        return json.Data as any[];
    };
    const getFacultyMemberResources = async () => {
        const res = await fetch("/AtelierApplication/GetFacultyMemberResourcesJson");
        const json = await res.json();
        return json.Data;
    };
    const getFacultyMember = async (id: number) => {
        const res = await fetchGet("/AtelierApplication/GetFacultyMemberJson", { id });
        const json = await res.json();
        return json.Data;
    };
    const getAtelierImage = async (id: number) => {
        const res = await fetchGet("/AtelierApplication/GetAtelierImageJson", { id });
        const json = await res.json();
        return json.Data as AtelierImage;
    };
    const getAtelierImages = async (id: number) => {
        const res = await fetchGet("/AtelierApplication/GetAtelierImagesJson", { id });
        const json = await res.json();
        return json.Data as AtelierImage[];
    };
    const getInstructorWorks = async (id: number) => {
        const images = await getAtelierImages(id);
        return images.filter(x => x.ImageType == ImageType.InstructorWork);
    };
    const getStudentWorks = async (id: number) => {
        const images = await getAtelierImages(id);
        return images.filter(x => x.ImageType == ImageType.StudentWork);
    };
    const getOtherImages = async (id: number) => {
        const images = await getAtelierImages(id);
        return images.filter(x => x.ImageType == ImageType.Photos);
    };
    const loadFaculty = async ({ navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"]
            ]);
            const session = (await getApplicationStatusJson()).Data as UserSession;
            const facultyMembers = await getFacultyMembers(session.atelierId as number);
            return {
                session,
                facultyMembers
            };
        } catch (ex) {
            log("loadFaculty", { ex });
            navigate("/error", { state: { ex }});
        }
    }
    const getArtwork = async (id: number) => {
        const response = await fetchGet("/AtelierApplication/GetArtworkJson", { id });
        const json = await response.json();
        return json.Data;
    }
    const getArtworkImage = async (artworkId: number, newImageId: number) => {
        const response = await fetchGet("/AtelierApplication/GetArtworkImageJson", { artworkId, newImageId });
        const json = await response.json();
        return json.Data as ArtworkImageData;
    }
    const getCreditCardInfo = async () => {
        const response = await fetch("/AtelierApplication/GetCreditCardJson");
        const json = await response.json();
        return json.Data;
    }
    const loadCreditCard = async ({ navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"],
                [Claim.HasMinimumFaculty, "/faculty"],
                [Claim.HasMinimumInstructorWorks, "/instructor-works"],
                [Claim.HasMinimumStudentWorks, "/student-works"],
                [Claim.HasMinimumOtherImages, "/other-images"]
            ]);
            const info = await getCreditCardInfo();
            return info;
        } catch (ex) {
            log("loadCreditCard", { ex });
            navigate("/error");
        }
    };
    const loadArtworkImage = async ({ params, navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/details");
            }
            if (!params.artworkId) {
                return navigate("/not-found");
            }
            if (!params.newImageId) {
                return {
                    IsPrimary: false,
                    IsDeleted: false,
                    Artwork: {
                        Id: params.artworkId
                    }
                };
            }
            const artworkImage = await getArtworkImage(params.artworkId, params.newImageId);
            return artworkImage;
        }
        catch (ex) {
            log("fetchArtworkImageData", { ex });
            navigate("/error", { state: { ex }});
        }
    };
    const  loadIntroduction = async ({ navigate }: any) => {
        try {
            const session = await getApplicationStatusJson();
            log("fetchIntroduction",  { session});
            return { session: session.Data };
        } catch (ex) {
            log("fetchIntroduction", { ex });
            navigate("/error", { state: { ex }});
        }
    }
    const log = (message: string, data: Record<string,any>) => {
        console.log(message, data);
    }
    const loadFacultyMember = async ({ params, navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"]
            ]);
            const session = (await getApplicationStatusJson()).Data;            
            const atelierId = session.atelierId;
            const resources = await getFacultyMemberResources();
            if (params.id == undefined) {
                return {
                    session,
                    resources,
                    facultyMember: {
                        AtelierId: atelierId
                    }
                };
            }            
            const id = params.id as number;
            const facultyMember = await getFacultyMember(id);
            return {
                session,
                resources,
                facultyMember
            };
        } catch (ex) {
            log("loadFacultyMember", { ex });
            navigate("/error");
        }
    };
    const loadInstructorWork = async ({ params, navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"]
            ]);
            const session = (await getApplicationStatusJson()).Data;            
            const atelierId = session.atelierId;
            if (params.id == undefined) {
                return {
                    session,
                    atelierImage: {
                        AtelierId: atelierId,
                        ImageType: ImageType.InstructorWork
                    }
                };
            }            
            const id = params.id as number;
            const atelierImage = await getAtelierImage(id);
            return {
                session,
                atelierImage: {
                    ...atelierImage,
                    Name: atelierImage.NewImage.Name,
                    Description: atelierImage.NewImage.Description
                }
            };
        } catch (ex) {
            log("loadAtelierImage", { ex });
            navigate("/error");
        }
    };
    const loadStudentWork = async ({ params, navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"],
                [Claim.HasMinimumInstructorWorks, "/instructor-works"]
            ]);
            const session = (await getApplicationStatusJson()).Data;            
            const atelierId = session.atelierId;
            if (params.id == undefined) {
                return {
                    session,
                    atelierImage: {
                        AtelierId: atelierId,
                        ImageType: ImageType.StudentWork
                    }
                };
            }            
            const id = params.id as number;
            const atelierImage = await getAtelierImage(id);
            return {
                session,
                atelierImage: {
                    ...atelierImage,
                    Name: atelierImage.NewImage.Name,
                    Description: atelierImage.NewImage.Description
                }
            };
        } catch (ex) {
            log("loadAtelierImage", { ex });
            navigate("/error");
        }
    };
    const loadOtherImage = async ({ params, navigate }: any) => {
        try {
            await authenticate(navigate, [
                [Claim.IsLoggedIn, "/not-logged-in"],
                [Claim.IsRegistered, "/details"],
                [Claim.HasMinimumInstructorWorks, "/instructor-works"],
                [Claim.HasMinimumStudentWorks, "/student-works"]
            ]);
            const session = (await getApplicationStatusJson()).Data;            
            const atelierId = session.atelierId;
            if (params.id == undefined) {
                return {
                    session,
                    atelierImage: {
                        AtelierId: atelierId,
                        ImageType: ImageType.Photos
                    }
                };
            }            
            const id = params.id as number;
            const atelierImage = await getAtelierImage(id);
            return {
                session,
                atelierImage: {
                    ...atelierImage,
                    Name: atelierImage.NewImage.Name,
                    Description: atelierImage.NewImage.Description
                }
            };
        } catch (ex) {
            log("loadAtelierImage", { ex });
            navigate("/error");
        }
    };
    const preload = async () => {
        /**
         * CMS
         */
        await loadDetailsCms();
        await loadFacultyCms();
        await loadPaymentCms();
        /**
         * Resources
         */
        await getProfileResources();
    };
    const loadPaypalCancelledData = async({ navigate }: any) => {
        try {
            // const params = useParams();
            // const token = params.token;
            toast.error("The Paypal transaction was cancelled on the Paypal website.");
            return loadInstructorWorks({ navigate });
        }catch (ex) {
            log("loadPaypalCancelledData", { ex });
            navigate("/error", { state: ex });
        }
    }
    const getUrlSearchParams = () => new URLSearchParams(window.location?.hash?.split("?")[1]);
    const loadPaypalConfirmedData = async({ navigate }: any) => {
        try {
            const params = getUrlSearchParams();
            console.log("loadPaypalConfirmedData", { params });
            return {
                token: params.get("token"),
                paymentId: params.get("paymentId"),
                payerId: params.get("PayerID")
            };
        } catch (ex) {  
            log("loadPaypalConfirmedData", { ex });
            navigate("/error", { state: ex });
        }
    }
    onMount(async() => {
        await preload();
    });
    return (
    <Router source={hashIntegration()}>
        <div class="page-container">
            <CMSProvider cms={cms} setCms={setCms}>
                <Toaster />
                <JetMenu></JetMenu>
                <LogoSlider></LogoSlider>
                <section id="header" style="padding-left: 40px; padding-top: 20px; padding-bottom: 20px;">
                    {headerContent()}
                </section>
                <div class="container" style="padding-bottom: 40px;">
                    {/* <ErrorBoundary fallback={err => {
                        return (
                            <div class="blank-page">{err.toString()}</div>
                        )
                    }}> */}
                        <Routes>
                            <GoogleMapsProvider apiKey="AIzaSyAweLj0DgOj1Em6hgX0-yPuOmwlvXLCJ2c">
                                    <Route path={["/details", "step-1"]} component={Details} data={loadDetails}></Route>
                                    <Route path={["/faculty", "step-2"]} component={Faculty} data={loadFaculty}></Route>
                                    <Route path={["/instructor-works", "step-3"]} component={InstructorWorks} data={loadInstructorWorks}></Route>
                                    <Route path={["/student-works", "step-4"]} component={StudentWorks} data={loadStudentWorks}></Route>
                                    <Route path={["/other-images", "step-5"]} component={OtherImages} data={loadOtherImages}></Route>
                                    <Route path={["/add-faculty-member","/edit-faculty-member/:id"]} component={EditFacultyMember} data={loadFacultyMember}></Route>
                                    <Route path={["/add-instructor-work","/edit-instructor-work/:id"]} component={EditAtelierImage} data={loadInstructorWork}></Route>
                                    <Route path={["/add-student-work","/edit-student-work/:id"]} component={EditAtelierImage} data={loadStudentWork}></Route>
                                    <Route path={["/add-other-image","/edit-other-image/:id"]} component={EditAtelierImage} data={loadOtherImage}></Route>
                                    <Route path={["/add-image-to-artwork/:artworkId","/edit-image/:artworkId/:newImageId"]} component={EditImage} data={loadArtworkImage}></Route>
                                    <Route path={"/payment"} component={Payment} data={loadPayment}></Route>
                                    <Route path={"/complete"} component={Complete} data={loadComplete}></Route>
                                    <Route path={"/payment/credit-card"} component={CreditCardPage} data={loadCreditCard}></Route>
                                    <Route path={"/payment/paypal-confirmed"} component={PaypalConfirmedPage} data={loadPaypalConfirmedData}></Route>
                                    <Route path={"/payment/paypal-cancelled"} component={Payment} data={loadPaypalCancelledData}></Route>
                                    <Route path={"/payment/processed"} component={PaymentProcessedPage}></Route>
                                    <Route path={"/not-logged-in"} component={() => {
                                        return (
                                            <div class="blank-page">
                                                <p>
                                                    You must be logged in to access this page.
                                                </p>
                                                <p>
                                                    Please <a class="tape" href="/Home/Login">log in</a> to your ARC account or <a class="tape" href="/Home/Register">register</a> one to proceed.
                                                </p>
                                            </div>                                    
                                        )
                                    }}></Route>
                                    <Route path={"/not-found"} component={() => {
                                        return (
                                            <div class="blank-page">
                                                Sorry, that page was not found
                                            </div>
                                        )
                                    }}></Route>
                                    <Route path={"/not-authorised"} component={() => {
                                        return (
                                            <div class="blank-page">
                                                You are not authorised to access the page at this time.
                                            </div>
                                        );
                                    }}></Route>
                                    <Route path={"/error"} component={() => {
                                        const params = useParams();
                                        return (
                                            <div class="blank-page">
                                                <h2>Sorry, there has been an error</h2>
                                                <div>{params.ex?.toString()}</div>
                                            </div>
                                        )
                                    }}></Route>
                                    <Route path={["/introduction","/"]} component={Introduction} data={loadIntroduction}></Route>
                            </GoogleMapsProvider>
                        </Routes> 
                    {/* </ErrorBoundary> */}
                </div>
                <Footer></Footer>
            </CMSProvider>
        </div>
    </Router>);
}